'use client'

import Box from '@mui/material/Box'
import type { StackProps } from '@mui/material/Stack'
import Stack from '@mui/material/Stack'
import type { ReactNode } from 'react'

export type GapSize = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const gapMap: Record<GapSize, number> = {
  none: 0,
  xs: 0.5, // 4px
  sm: 1, // 8px  — dense/related elements
  md: 2, // 16px — default gap between elements
  lg: 3, // 24px
  xl: 4, // 32px — between page sections
}

type Justify = 'start' | 'center' | 'end' | 'between' | 'around'
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline'

const justifyMap: Record<Justify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
}

const alignMap: Record<Align, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
}

interface FlexBaseProps extends Omit<StackProps, 'direction' | 'spacing'> {
  children?: ReactNode
  gap?: GapSize
  justify?: Justify
  align?: Align
  fullWidth?: boolean
  flex?: number | string
}

interface FlexRowProps extends FlexBaseProps {
  wrap?: boolean
}

export function FlexRow({
  children,
  gap = 'sm',
  justify,
  align = 'center',
  wrap = false,
  fullWidth = false,
  flex,
  ...props
}: FlexRowProps) {
  return (
    <Stack
      direction="row"
      {...props}
      sx={[
        {
          alignItems: alignMap[align],
          justifyContent: justify ? justifyMap[justify] : undefined,
          gap: gapMap[gap],
          flexWrap: wrap ? 'wrap' : 'nowrap',
          width: fullWidth ? '100%' : undefined,
          flex: flex,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      {children}
    </Stack>
  )
}

export function FlexCol({
  children,
  gap = 'md',
  justify,
  align = 'stretch',
  fullWidth = false,
  flex,
  ...props
}: FlexBaseProps) {
  return (
    <Stack
      direction="column"
      {...props}
      sx={[
        {
          alignItems: alignMap[align],
          justifyContent: justify ? justifyMap[justify] : undefined,
          gap: gapMap[gap],
          width: fullWidth ? '100%' : undefined,
          flex: flex,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      {children}
    </Stack>
  )
}

/** Flexible filler that pushes siblings apart inside FlexRow/FlexCol. */
export function Spacer() {
  return <Box sx={{ flex: 1 }} />
}
